// DailyDashboard — orchestrates the daily-view UI for a PJ Daily Solution
// run. Owns activeModel/activeDay/selected state; delegates rendering to
// modular sub-components (chips, day pills, sankey, side panels, detail).
//
// Data path: parent passes the run.result_tables.master_production_plan rows
// + the pre-built run.result_tables.sankey payload. We:
//   - re-aggregate the sankey from MPP rows for the active day (so day filter
//     reshapes the chart without a backend roundtrip);
//   - keep the existing pre-built payload's `models` + `stages` for fallback
//     when a model has no MPP rows in the active slice.

import { useMemo, useState } from 'react';
import { SankeyChart, type SankeyPayload } from '../SankeyChart';
import { SummaryChips } from './SummaryChips';
import { DayFilter } from './DayFilter';
import { CriticalPOPanel } from './CriticalPOPanel';
import { UnfilledPanel } from './UnfilledPanel';
import { RmDonut } from './RmDonut';
import { SankeyDetailPanel } from './SankeyDetailPanel';
import {
  buildDetailContent,
  buildSankeyFromMPP,
  chipStats,
  filterRowsByModelDay,
  STAGES,
} from './mppHelpers';
import type { MPPRow, SelectedTarget } from './types';
import styles from './DailyDashboard.module.css';

type ExpandedPanel = 'crit' | 'unfilled' | 'donut' | 'detail' | null;

export function DailyDashboard({
  rows,
  fallbackPayload,
}: {
  rows: readonly MPPRow[];
  /** The pre-built sankey payload from the VM; used as a fallback for the
   *  list of available models when the MPP slice is empty. */
  fallbackPayload?: SankeyPayload | null;
}) {
  // Determine available models from MPP rows (fall back to payload if empty).
  const models = useMemo(() => {
    const fromMpp = Array.from(new Set(rows.map((r) => r.model_type))).filter(
      Boolean,
    );
    if (fromMpp.length > 0) return fromMpp as string[];
    return fallbackPayload?.models ?? [];
  }, [rows, fallbackPayload]);

  // Prefer the 5_day model when present — it's the most useful daily view
  // (solon_only has no multi-stage chain to draw; no_pos/full are overlay
  // comparisons). Falls back to the first available model otherwise.
  const [activeModel, setActiveModel] = useState<string>(
    () => (models.includes('5_day') ? '5_day' : models[0] ?? '5_day'),
  );
  const [activeDay, setActiveDay] = useState<string>('all');
  const [selected, setSelected] = useState<SelectedTarget | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<ExpandedPanel>(null);

  // Day-filtered slice and rebuilt sankey payload — the chart re-renders
  // from this on every day/model change.
  const sliceRows = useMemo(
    () => filterRowsByModelDay(rows, activeModel, activeDay),
    [rows, activeModel, activeDay],
  );

  const sankey = useMemo<SankeyPayload>(() => {
    const built = buildSankeyFromMPP(sliceRows);
    return {
      models: [activeModel],
      stages: [...STAGES],
      by_model: {
        [activeModel]: {
          nodes: built.nodes,
          links: built.links,
          totals: {
            lbs: built.links.reduce((a, l) => a + l.lbs, 0),
            orders: new Set(
              sliceRows.map((r) => r.order).filter(Boolean),
            ).size,
            links: built.links.length,
            nodes: built.nodes.length,
          },
        },
      },
    };
  }, [sliceRows, activeModel]);

  // Stats + detail panel content
  const stats = useMemo(
    () => chipStats(rows, activeModel, activeDay),
    [rows, activeModel, activeDay],
  );

  const detailContent = useMemo(() => {
    if (!selected) return null;
    const model = sankey.by_model[activeModel];
    if (!model) return null;
    // Rebuild the "laid" link/node shape from the controlled-source-only payload
    // so the helper can iterate without depending on d3 internals.
    const laidNodes = model.nodes.map((n) => ({
      id: n.id,
      stage: n.stage,
      label: n.label,
    }));
    const laidLinks = model.links.map((l) => {
      const s = model.nodes.find((n) => n.id === l.source);
      const t = model.nodes.find((n) => n.id === l.target);
      if (!s || !t) return null;
      return {
        source: { id: s.id, stage: s.stage, label: s.label },
        target: { id: t.id, stage: t.stage, label: t.label },
        lbs: l.lbs,
      };
    }).filter((x): x is NonNullable<typeof x> => x != null);
    // Compute highlight set (up + down)
    const hiNodes = new Set<string>();
    if (selected.kind === 'node') {
      const queue = [selected.nodeId];
      hiNodes.add(selected.nodeId);
      const visit = (id: string, dir: 'down' | 'up') => {
        for (const l of laidLinks) {
          const from = dir === 'down' ? l.source.id : l.target.id;
          const to = dir === 'down' ? l.target.id : l.source.id;
          if (from === id && !hiNodes.has(to)) {
            hiNodes.add(to);
            queue.push(to);
          }
        }
      };
      while (queue.length) {
        const cur = queue.shift()!;
        visit(cur, 'down');
        visit(cur, 'up');
      }
    }
    return buildDetailContent(selected, sliceRows, laidLinks, laidNodes, hiNodes);
  }, [selected, sankey, activeModel, sliceRows]);

  const onClearDetail = () => setSelected(null);
  const onExpand = (id: ExpandedPanel) =>
    setExpandedPanel((cur) => (cur === id ? null : id));

  const showDetail = selected != null && detailContent != null;

  return (
    <div className={styles.root}>
      {models.length > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Model overlay:
            <select
              value={activeModel}
              onChange={(e) => {
                setActiveModel(e.target.value);
                setSelected(null);
                setActiveDay('all');
              }}
              style={{ marginLeft: 6, padding: '3px 6px' }}
            >
              {models.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <SummaryChips stats={stats} model={activeModel} day={activeDay} />

      <DayFilter
        rows={rows}
        activeModel={activeModel}
        activeDay={activeDay}
        onChange={(d) => {
          setActiveDay(d);
          setSelected(null);
        }}
      />

      <div className={styles.split}>
        <div style={{ minWidth: 0 }}>
          <SankeyChart
            payload={sankey}
            activeModel={activeModel}
            embedded
            selected={selected}
            onSelect={setSelected}
          />
        </div>

        <div className={styles.sideCol}>
          {showDetail && detailContent ? (
            <SankeyDetailPanel
              content={detailContent}
              onBack={onClearDetail}
              onExpand={() => onExpand('detail')}
            />
          ) : (
            <>
              <CriticalPOPanel
                rows={rows}
                activeModel={activeModel}
                activeDay={activeDay}
                onExpand={() => onExpand('crit')}
              />
              <UnfilledPanel
                rows={rows}
                activeModel={activeModel}
                onExpand={() => onExpand('unfilled')}
              />
              <RmDonut
                rows={sliceRows}
                onExpand={() => onExpand('donut')}
              />
            </>
          )}
        </div>
      </div>

      {expandedPanel && (
        <ExpandedOverlay
          panel={expandedPanel}
          rows={rows}
          sliceRows={sliceRows}
          activeModel={activeModel}
          activeDay={activeDay}
          detailContent={detailContent}
          onClose={() => setExpandedPanel(null)}
          onClearDetail={onClearDetail}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expanded-panel overlay — shows whichever panel was expanded at large size,
// dimming the rest of the dashboard behind a backdrop.
// ---------------------------------------------------------------------------

function ExpandedOverlay({
  panel,
  rows,
  sliceRows,
  activeModel,
  activeDay,
  detailContent,
  onClose,
  onClearDetail,
}: {
  panel: ExpandedPanel;
  rows: readonly MPPRow[];
  sliceRows: readonly MPPRow[];
  activeModel: string;
  activeDay: string;
  detailContent: ReturnType<typeof buildDetailContent>;
  onClose: () => void;
  onClearDetail: () => void;
}) {
  return (
    <>
      <div className={styles.expandBackdrop} onClick={onClose} />
      <div className={styles.cardExpanded}>
        {panel === 'crit' && (
          <CriticalPOPanel
            rows={rows}
            activeModel={activeModel}
            activeDay={activeDay}
          />
        )}
        {panel === 'unfilled' && (
          <UnfilledPanel rows={rows} activeModel={activeModel} fill />
        )}
        {panel === 'donut' && <RmDonut rows={sliceRows} expanded />}
        {panel === 'detail' && detailContent && (
          <SankeyDetailPanel
            content={detailContent}
            onBack={() => {
              onClearDetail();
              onClose();
            }}
          />
        )}
      </div>
    </>
  );
}
